import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Container, Group, Image, Paper, PasswordInput, Stack, Text, Title } from '@mantine/core'
import { IconCircleCheckFilled } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useUpdatePasswordMutation } from '../../api/auth/api'
import { RouterButton } from '../../components/RouterButton'
import { isPasswordValid, PasswordValidationRules } from '../../utils/password-validation'

const schema = z
  .object({
    password: z
      .string()
      .min(
        PasswordValidationRules.minLength,
        `Password must be at least ${PasswordValidationRules.minLength} characters`,
      )
      .max(
        PasswordValidationRules.maxLength,
        `Password must be ${PasswordValidationRules.maxLength} characters or less`,
      )
      .regex(PasswordValidationRules.hasUpperCase, 'Password must contain at least 1 uppercase letter')
      .regex(PasswordValidationRules.hasLowerCase, 'Password must contain at least 1 lowercase letter')
      .regex(PasswordValidationRules.hasNumber, 'Password must contain at least 1 number')
      .regex(PasswordValidationRules.hasSpecialChar, 'Password must contain at least 1 special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  })

type FormData = z.infer<typeof schema>

export const Route = createFileRoute('/recovery/reset-v2')({
  component: ResetPasswordScreen,
  validateSearch: z.object({
    email: z.string(),
    code: z.string(),
  }),
})

function ResetPasswordScreen() {
  const [success, setSuccess] = useState(false)
  const { email, code } = Route.useSearch()

  const { mutate: updatePassword, isPending } = useUpdatePasswordMutation()

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const onSubmit = handleSubmit((data) => {
    updatePassword(
      {
        email,
        code,
        password: data.password,
      },
      {
        onSuccess: () => {
          setSuccess(true)
        },
      },
    )
  })

  if (success) {
    return (
      <Stack
        align="center"
        justify="center"
        style={{
          height: '100vh',
          width: '100%',
          background: 'linear-gradient(135deg, #ff8800 0%, #ff6600 35%, #ff4400 100%)',
        }}
      >
        <Container size={600}>
          <Paper withBorder shadow="md" p={30} mt={30} radius="md" w={500}>
            <Image h={45} w="auto" fit="contain" src="/carma_partner_logo.svg" alt="Carma Logo" />
            <Title order={3} style={{ fontWeight: 700, color: '#333' }} mt={30}>
              Password reset successful
            </Title>
            <Text size="sm" mb={30}>
              Please log in with your updated password
            </Text>
            <RouterButton fullWidth to="/login">
              Return to login
            </RouterButton>
          </Paper>
        </Container>
      </Stack>
    )
  }

  return (
    <Stack
      align="center"
      justify="center"
      style={{
        height: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #ff8800 0%, #ff6600 35%, #ff4400 100%)',
      }}
    >
      <Container size={600}>
        <Paper withBorder shadow="md" p={30} mt={30} radius="md" w={500}>
          <Image h={45} w="auto" fit="contain" src="/carma_partner_logo.svg" alt="Carma Logo" />
          <Title order={3} style={{ fontWeight: 700, color: '#333' }} mt={30}>
            Reset your password
          </Title>
          <form onSubmit={onSubmit}>
            <PasswordInput
              label="New password"
              placeholder="Your password"
              required
              mt={20}
              {...register('password')}
              error={errors.password?.message}
            />
            <Box mt={10} mb={10}>
              <Group gap={8}>
                <IconCircleCheckFilled
                  size={16}
                  style={{
                    color:
                      watch('password').length >= PasswordValidationRules.minLength &&
                      watch('password').length <= PasswordValidationRules.maxLength
                        ? 'green'
                        : '#CCCCCC',
                  }}
                />
                <Text size="xs">
                  Be between {PasswordValidationRules.minLength} and {PasswordValidationRules.maxLength} characters long
                </Text>
              </Group>
              <Group gap={8} mt={4}>
                <IconCircleCheckFilled
                  size={16}
                  style={{ color: PasswordValidationRules.hasUpperCase.test(watch('password')) ? 'green' : '#CCCCCC' }}
                />
                <Text size="xs">Contain at least 1 uppercase letter</Text>
              </Group>
              <Group gap={8} mt={4}>
                <IconCircleCheckFilled
                  size={16}
                  style={{ color: PasswordValidationRules.hasLowerCase.test(watch('password')) ? 'green' : '#CCCCCC' }}
                />
                <Text size="xs">Contain at least 1 lowercase letter</Text>
              </Group>
              <Group gap={8} mt={4}>
                <IconCircleCheckFilled
                  size={16}
                  style={{
                    color: PasswordValidationRules.hasSpecialChar.test(watch('password')) ? 'green' : '#CCCCCC',
                  }}
                />
                <Text size="xs">Contain at least 1 special character</Text>
              </Group>
              <Group gap={8} mt={4}>
                <IconCircleCheckFilled
                  size={16}
                  style={{ color: PasswordValidationRules.hasNumber.test(watch('password')) ? 'green' : '#CCCCCC' }}
                />
                <Text size="xs">Contain at least 1 number</Text>
              </Group>
            </Box>
            <PasswordInput
              label="Confirm new password"
              placeholder="Enter your password again"
              required
              {...register('confirmPassword')}
              error={errors.confirmPassword?.message}
            />
            <Group gap={8} justify="center" mt="xl">
              <RouterButton w={'49%'} variant="outline" to="/login">
                Return to login
              </RouterButton>
              <Button
                w={'49%'}
                type="submit"
                loading={isPending}
                disabled={!isPasswordValid(watch('password')) || watch('password') !== watch('confirmPassword')}
              >
                Reset Password
              </Button>
            </Group>
          </form>
        </Paper>
      </Container>
    </Stack>
  )
}
